import type { ITheme } from "@fluentui/react/lib/Styling";
import { mergeStyleSets } from "@fluentui/react/lib/Styling";
import { memoizeFunction } from "@fluentui/react/lib/Utilities";

import type {
  AppColors,
  IContextualMenuClassNames,
  IMenuItemClassNames,
} from "@1js/acui-common";
import {
  baseFabricFocusAfterSelector,
  getContextualMenuClassNames,
  getInnerBorderStyles,
  getItemClassNames,
  getPrimaryControlSemanticColorsWithTheme,
  getStandardControlSemanticColorsWithTheme,
} from "@1js/acui-common";
import { isThemingEnabled } from "@1js/acui-theme";

const textColor = "#434343";
const hoverBackgroundColor = "#e1e1e1";
const pressedBackgroundColor = "#d2d2d2";
const overflowMenuBackgroundColor = "#f3f3f3";

export const getOverflowMenuClassNames = memoizeFunction(
  (
    appColors: AppColors,
    responsiveClassName?: string
  ): ((theme: ITheme) => IContextualMenuClassNames) => {
    return (theme: ITheme): IContextualMenuClassNames => {
      const semanticColors = getStandardControlSemanticColorsWithTheme(theme);
      let styleSets = mergeStyleSets(
        getContextualMenuClassNames(theme, appColors),
        {
          root: [
            {
              minWidth: 82,
              backgroundColor: isThemingEnabled()
                ? semanticColors.defaultBackgroundColor
                : overflowMenuBackgroundColor,
            },
          ],
        }
      ) as IContextualMenuClassNames;

      if (responsiveClassName) {
        styleSets = {
          ...styleSets,
          root: `${styleSets.root} ${responsiveClassName}`,
        };
      }
      return styleSets;
    };
  }
);

export const getOverflowMenuItemClassNames = memoizeFunction(
  (
    appColors: AppColors
  ): ((theme: ITheme, disabled: boolean) => IMenuItemClassNames) => {
    return (theme: ITheme, disabled: boolean): IMenuItemClassNames => {
      const semanticColors = getStandardControlSemanticColorsWithTheme(theme);
      const primaryColors = getPrimaryControlSemanticColorsWithTheme(theme);
      const themingEnabled = isThemingEnabled();
      const overflowTextColor = themingEnabled
        ? semanticColors.textColor
        : textColor;
      return mergeStyleSets(
        getItemClassNames(
          theme,
          appColors,
          disabled,
          false /* expanded */,
          false /* checked */,
          false /* isAnchorLink */,
          false /* knownIcon */
        ),
        {
          root: {
            color: overflowTextColor,
            selectors: {
              ":hover": {
                backgroundColor: themingEnabled
                  ? semanticColors.hoverBackgroundColor
                  : hoverBackgroundColor,
                color: overflowTextColor,
              },
              ":active": {
                backgroundColor: themingEnabled
                  ? semanticColors.pressedBackgroundColor
                  : pressedBackgroundColor,
                color: overflowTextColor,
              },
              [baseFabricFocusAfterSelector]: [
                getInnerBorderStyles({
                  width: 2,
                  color: semanticColors.focusedBorderColor,
                }),
                { outline: "none" },
              ],
            },
          },
          item: {
            color: themingEnabled
              ? primaryColors.primaryTextColor
              : appColors.primary,
          },
        }
      );
    };
  }
);
